import React from 'react';
import { Brain, AlertCircle, TrendingUp } from 'lucide-react';

const TriggerAnalysis = ({ triggers }) => {
  const getImpactColor = (impact) => {
    if (impact >= 5) return 'text-red-600 bg-red-50';
    if (impact >= 3) return 'text-yellow-600 bg-yellow-50';
    return 'text-green-600 bg-green-50';
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="p-3 bg-indigo-100 rounded-lg">
          <Brain className="w-6 h-6 text-indigo-600" />
        </div>
        <div>
          <h2 className="text-xl font-bold text-gray-900">Trigger Analysis</h2>
          <p className="text-sm text-gray-600">Identify and understand your symptom triggers</p>
        </div>
      </div>
      <div className="space-y-4">
        {triggers?.slice(0, 10)?.map((trigger, index) => (
          <div key={index} className="border border-gray-200 rounded-lg p-5 hover:border-indigo-300 transition-colors">
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900 text-lg capitalize">{trigger?.trigger}</h3>
                <p className="text-sm text-gray-600 mt-1">
                  Triggered {trigger?.count} times • Avg severity: {trigger?.avgSeverity?.toFixed(1)}/10
                </p>
              </div>
              <span className={`px-3 py-1 rounded-full text-xs font-medium ${getImpactColor(trigger?.impact)}`}>
                Impact: {trigger?.impact?.toFixed(1)}
              </span>
            </div>

            {/* Severity Distribution */}
            <div className="mb-4">
              <div className="text-sm font-medium text-gray-700 mb-2">Severity Range</div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-600">Low</span>
                <div className="flex-1 h-8 bg-gradient-to-r from-green-200 via-yellow-200 to-red-200 rounded relative">
                  <div
                    className="absolute top-0 bottom-0 w-1 bg-indigo-600"
                    style={{ left: `${(trigger?.avgSeverity / 10) * 100}%` }}
                    title={`Average: ${trigger?.avgSeverity?.toFixed(1)}`}
                  />
                  <div
                    className="absolute top-0 bottom-0 bg-indigo-600 opacity-20 rounded"
                    style={{
                      left: `${(Math.min(...trigger?.severities) / 10) * 100}%`,
                      right: `${100 - (Math.max(...trigger?.severities) / 10) * 100}%`
                    }}
                  />
                </div>
                <span className="text-xs text-gray-600">High</span>
              </div>
              <div className="flex justify-between text-xs text-gray-500 mt-1">
                <span>Min: {Math.min(...trigger?.severities)}</span>
                <span>Avg: {trigger?.avgSeverity?.toFixed(1)}</span>
                <span>Max: {trigger?.maxSeverity}</span>
              </div>
            </div>

            {/* Associated Symptoms */}
            {Object.keys(trigger?.symptoms || {})?.length > 0 && (
              <div>
                <div className="text-sm font-medium text-gray-700 mb-2">Associated Symptoms</div>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(trigger?.symptoms)
                    ?.sort((a, b) => b?.[1] - a?.[1])
                    ?.slice(0, 5)
                    ?.map(([symptom, count]) => (
                      <span
                        key={symptom}
                        className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-xs font-medium"
                      >
                        {symptom} ({count})
                      </span>
                    ))}
                </div>
              </div>
            )}
          </div>
        ))}

        {(!triggers || triggers?.length === 0) && (
          <div className="text-center py-8">
            <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-600">No trigger data available</p>
            <p className="text-sm text-gray-500 mt-1">Track symptoms with triggers to see analysis</p>
          </div>
        )}
      </div>
      {triggers?.length > 0 && (
        <div className="mt-6 p-4 bg-indigo-50 border border-indigo-200 rounded-lg">
          <div className="flex items-start gap-3">
            <TrendingUp className="w-5 h-5 text-indigo-600 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-indigo-900">
              <strong>Insight:</strong> Focus on avoiding or managing the top 3 triggers to potentially reduce
              symptom frequency by up to {((triggers?.[0]?.count / triggers?.reduce((sum, t) => sum + t?.count, 0)) * 100)?.toFixed(0)}%.
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TriggerAnalysis;